import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchCollectionWithPost, useFetchResource } from '@app/src/api/fetchHooks'
import { Permissions, usePermissions } from '@app/src/auth/permissions'
import { useDialogState } from '@app/src/hooks/mui-hooks'
import { useQueryState } from '@app/src/hooks/queryState'
import { repositoryCopyKey } from '@app/src/pages/Questionnaire/Provider/Actions/SaveFilesToRepository/responseFiles'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { SortOrder } from '@app/src/types/filter'
import type { Product } from '@app/src/types/product'
import type {
  QuestionRule,
  ReportAccessModel,
  Request,
  RequestHistory,
  ResponseItem,
  ResponseWithPreviousCorrectionNeededItem,
} from '@app/src/types/resourceExplorer'
import { HistoryStatus, ResponseDraftStatus } from '@app/src/types/resourceExplorer'
import { areResponsesEqual } from '@app/src/utils/compareResponseItems'
import { distinctBy } from '@app/src/utils/helpersTs'
import { createTargetProductFilter } from '@app/src/utils/targetProductFilters'
import { dashboard } from '@app/src/wf-constants/paths'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useHistory } from 'react-router'
import type { LanguageInfo, ReportContextProps } from '../ReportContext'

export enum ReuseAllQuestionsStates {
  ApplyAll = 'ApplyAll',
  Review = 'Review',
}

export const useReportContext = () => {
  const history = useHistory()
  const [isSuccessSubmitDialogOpen, openSuccessSubmitDialog, closeSuccessSubmitDialog] = useDialogState()
  const [isSaveFilesStepOpen, openSaveFilesStep, closeSaveFilesStep] = useDialogState()
  const [previouslySubmittedItems, setPreviouslySubmittedItems] = useState<ResponseItem[]>([])
  const [repositoryCopyKeys, setRepositoryCopyKeys] = useState<ReadonlySet<string>>(new Set())
  const markedRepositoryCopyKeys = useRef(new Set<string>())
  const [requestId, setRequestId] = useQueryState('requestId')
  const [responseId, setResponseId] = useQueryState('responseId')
  const [showAccessNudge, setShowAccessNudge] = useState(false)
  const [isLoadingAccessNudge, setLoadingAccessNudge] = useState(true)
  const [reuseAllQuestionsState, setReuseAllQuestionsState] = useState<ReuseAllQuestionsStates>(
    ReuseAllQuestionsStates.Review,
  )
  const [showPreviousAnswerSuggestions, setShowPreviousAnswerSuggestions] = useState(true)

  const [locale, setLocale] = useState<LanguageInfo | null>(null)
  const { hasPermission } = usePermissions()

  const isTransparency = hasPermission(Permissions.TRANSPARENCY_USER)

  const {
    data: request,
    isLoading: isRequestLoading,
    isError: isRequestError,
  } = useFetchResource<Request>({
    endpoint: endpoints.request(requestId, locale?.code),
    key: [FetchKey.Request, Number(requestId), locale?.code],
    options: {
      enabled: Boolean(requestId),
    },
  })

  useEffect(() => {
    if (isRequestError) {
      history.replace(dashboard)
    }
  }, [isRequestError, history])

  const productId = request?.targetProductId

  const getResponderFilter = (request?: Request) => {
    if (!request) return null
    const key = request.responderOrganizationId ? 'responderOrganizationId' : 'subscriptions.targetProviderId'
    const value = request.responderOrganizationId ?? request.subscriptions?.[0]?.targetProviderId

    return {
      name: key,
      filters: [{ value, operator: Operators.EqualTo }],
    }
  }

  const responderFilter = useMemo(() => getResponderFilter(request), [request])

  const languageToTranslateResponseTo = isTransparency ? undefined : locale?.code
  const {
    data: response,
    isLoading: isResponseLoading,
    isFetching: isResponseFetching,
    isError: isResponseError,
  } = useFetchResource<ResponseWithPreviousCorrectionNeededItem>({
    endpoint: endpoints.response(responseId, languageToTranslateResponseTo, isTransparency),
    key: [FetchKey.Response, Number(responseId), languageToTranslateResponseTo],
    options: {
      enabled: Boolean(responseId),
    },
  })

  useEffect(() => {
    if (isResponseError) {
      setResponseId(undefined, true)
    }
  }, [isResponseError, setResponseId])

  const { data: currentAccess, isLoading: isCurrentAccessLoading } = useFetchResource<ReportAccessModel>({
    key: [FetchKey.ResponseSharing, Number(requestId)],
    endpoint: endpoints.getAccessForReport(requestId),
    options: { enabled: Boolean(requestId) },
  })

  const { data: requestHistory, isLoading: isRequestHistoryLoading } = useFetchResource<RequestHistory[]>({
    key: [FetchKey.RequestHistory, Number(requestId)],
    endpoint: endpoints.requestHistory(requestId),
    options: {
      enabled: Boolean(requestId),
    },
  })

  // Fetch the latest submitted response to compare with draft
  const latestSubmittedResponseId = useMemo(() => {
    return requestHistory?.find(h => h.status === HistoryStatus.Submitted)?.responseId
  }, [requestHistory])

  const { data: latestSubmittedResponse } = useFetchResource<ResponseWithPreviousCorrectionNeededItem>({
    endpoint: endpoints.response(latestSubmittedResponseId, languageToTranslateResponseTo, isTransparency),
    key: [FetchKey.Response, Number(latestSubmittedResponseId), languageToTranslateResponseTo],
    options: {
      enabled: Boolean(latestSubmittedResponseId) && response?.draftStatus === ResponseDraftStatus.Draft,
    },
  })

  const savedResponseHasChangesFromLastSubmitted = useMemo(() => {
    if (!latestSubmittedResponse) return false

    if (!response || response.draftStatus !== ResponseDraftStatus.Draft) return false

    return !areResponsesEqual(response, latestSubmittedResponse)
  }, [response, latestSubmittedResponse])

  const requestsPerPeriodFilters = useMemo(() => {
    const filters = [
      {
        name: 'questionnaireTemplateId',
        filters: [
          {
            value: request?.questionnaireTemplateId,
            operator: Operators.EqualTo,
          },
        ],
      },
    ]

    filters.push(createTargetProductFilter(productId, 'targetProductId'))

    if (responderFilter) {
      filters.push(responderFilter)
    }

    return filters
  }, [request?.questionnaireTemplateId, productId, responderFilter])

  const { items: requestsPerPeriod, isLoading: isRequestsPerPeriodLoading } = useFetchCollectionWithPost<Request>({
    key: productId ? [FetchKey.RequestPerPeriod, productId] : FetchKey.RequestPerPeriod,
    endpoint: endpoints.requests,
    payload: {
      include: productId ? ['responses', 'inquiries', 'inquiries.targetProduct'] : ['responses'],
      filter: requestsPerPeriodFilters,
      sort: {
        target: 'periodName',
        order: SortOrder.ASCENDING,
      },
    },
    options: {
      enabled: Boolean(request),
    },
  })

  // Get the product information if productId is provided
  const targetProduct = useMemo((): Product | undefined => {
    if (!productId || !requestsPerPeriod) return undefined

    const inquiryWithProduct = requestsPerPeriod
      .flatMap(req => req.inquiries || [])
      .find(inquiry => inquiry.targetProductId === productId && inquiry.targetProduct)
    return inquiryWithProduct?.targetProduct
  }, [requestsPerPeriod, productId])

  const requestItemsRules = useMemo(() => {
    return request?.sections
      .flatMap(section => section?.items.flatMap(item => item?.questionnaireQuestionRules))
      .filter((rule): rule is QuestionRule => rule !== undefined)
  }, [request])

  const latestHistory = useMemo(() => requestHistory?.[0], [requestHistory])
  const reportStatus = useMemo(
    () => (responseId !== latestHistory?.responseId ? response?.draftStatus : latestHistory?.status),
    [responseId, latestHistory, response],
  )

  const updateRequestIdParam: ReportContextProps['updateRequestIdParam'] = (requestId?) => {
    setRequestId(requestId, true)
  }

  const updateResponseIdParam: ReportContextProps['updateResponseIdParam'] = (responseId?) => {
    setResponseId(responseId, true)
  }

  useEffect(() => {
    if (!responseId && latestHistory?.responseId) {
      setResponseId(latestHistory.responseId, true)
    }
  }, [responseId, latestHistory])

  const uniqueSubscriberOrganizations = request
    ? distinctBy(
        request.subscriptions?.map(subscription => subscription?.creatorOrganization),
        org => org?.id,
      )
    : []

  const openPostSubmitFlow = () => {
    setPreviouslySubmittedItems(latestSubmittedResponse?.items ?? [])
    setRepositoryCopyKeys(new Set(markedRepositoryCopyKeys.current))
    openSaveFilesStep()
  }

  const markRepositoryCopy = (responseItemId: number, fileName: string) => {
    markedRepositoryCopyKeys.current.add(repositoryCopyKey(responseItemId, fileName))
  }

  const finishSaveFilesStep = () => {
    closeSaveFilesStep()
    openSuccessSubmitDialog()
  }

  const contextValue: ReportContextProps = {
    responseId: responseId ? Number(responseId) : undefined,
    requestId: requestId ? Number(requestId) : undefined,
    request,
    response,
    requestsPerPeriod,
    currentAccess,
    requestHistory,
    latestHistory,
    reportStatus,
    targetProduct,
    locale,
    showAccessNudge,
    isLoadingAccessNudge,
    isRequestLoading,
    isResponseLoading,
    isResponseFetching,
    isCurrentAccessLoading,
    isRequestHistoryLoading,
    isRequestsPerPeriodLoading,
    savedResponseHasChangesFromLastSubmitted,
    reuseAllQuestionsState,
    updateRequestIdParam,
    updateResponseIdParam,
    setReportLocale: setLocale,
    setShowAccessNudge,
    setLoadingAccessNudge,
    setReuseAllQuestionsState,
    showPreviousAnswerSuggestions,
    setShowPreviousAnswerSuggestions,
    openPostSubmitFlow,
    markRepositoryCopy,
    requestItemsRules,
    isPreview: false,
  }

  return {
    contextValue,
    dialogState: {
      isSuccessSubmitDialogOpen,
      closeSuccessSubmitDialog,
      isSaveFilesStepOpen,
      finishSaveFilesStep,
      previouslySubmittedItems,
      repositoryCopyKeys,
    },
    uniqueSubscriberOrganizations,
  }
}
